/*
  Cart promo popup — "buy N, get one free" nudge (sections/cart-promo-popup.liquid).

  Opens once the cart holds exactly N products from the promo collection, so the
  customer can pick the last one themselves. We never auto-add: we don't know which
  flavour they want.

  Eligibility comes from the section (Liquid), because /cart.js line items carry no
  collections and no tags. Liquid caps the product loop, so when the collection is
  bigger than what it emitted we top the list up once from the collection's JSON
  endpoint and cache it for the session.
*/
(function () {
  'use strict';

  var SESSION_SHOWN_KEY = 'cart_promo_popup_shown';
  var SESSION_IDS_KEY = 'cart_promo_popup_ids';

  var root = document.getElementById('CartPromoPopup');
  if (!root) return;

  var dialog = root.querySelector('.cart-promo-popup__dialog');
  var lastFocused = null;
  var idSet = null;
  var topUpPromise = null;
  var debounceTimer = null;
  var isOpen = false;
  var lastQty = null;

  function config() {
    return window.CART_PROMO_POPUP || {};
  }

  function hasCookie(name) {
    return document.cookie.split('; ').some(function (c) {
      return c.indexOf(name + '=') === 0;
    });
  }

  function sessionGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function sessionSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      /* private mode — the popup simply shows again next navigation */
    }
  }

  /* ===== Eligible product IDs ===== */

  function baseIds() {
    var ids = config().productIds;
    return Array.isArray(ids) ? ids : [];
  }

  /*
    Liquid truncates long collections. Only then do we pay for the extra request,
    and only once per session.
  */
  function needsTopUp() {
    var cfg = config();
    return Number(cfg.collectionCount || 0) > baseIds().length && !!cfg.collectionHandle;
  }

  function topUpIds() {
    if (topUpPromise) return topUpPromise;

    var cached = sessionGet(SESSION_IDS_KEY);
    if (cached) {
      try {
        var parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length) {
          topUpPromise = Promise.resolve(parsed);
          return topUpPromise;
        }
      } catch (e) {
        /* fall through to a fresh fetch */
      }
    }

    topUpPromise = fetch('/collections/' + config().collectionHandle + '/products.json?limit=250')
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        if (!data || !Array.isArray(data.products)) return baseIds();
        var ids = data.products.map(function (p) {
          return p.id;
        });
        // Union: the JSON endpoint is capped too, so never drop what Liquid gave us.
        var merged = baseIds().concat(ids);
        sessionSet(SESSION_IDS_KEY, JSON.stringify(merged));
        return merged;
      })
      .catch(function () {
        return baseIds();
      });

    return topUpPromise;
  }

  function getIdSet() {
    if (idSet) return Promise.resolve(idSet);

    if (!needsTopUp()) {
      idSet = new Set(baseIds());
      return Promise.resolve(idSet);
    }

    return topUpIds().then(function (ids) {
      idSet = new Set(ids);
      return idSet;
    });
  }

  /* ===== Cart ===== */

  function getCart() {
    // No cart cookie → guaranteed empty cart, skip the round-trip (same shortcut
    // the mini-cart uses in snippets/cart-drawer.liquid).
    if (!hasCookie('cart')) return Promise.resolve({ items: [] });
    return fetch('/cart.js')
      .then(function (res) {
        return res.json();
      })
      .catch(function () {
        return { items: [] };
      });
  }

  function eligibleQty(cart, ids) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce(function (sum, item) {
      // Never count an auto-added free gift towards the promo.
      if (item.properties && item.properties._free_gift) return sum;
      return ids.has(item.product_id) ? sum + Number(item.quantity || 0) : sum;
    }, 0);
  }

  /* ===== Open / close ===== */

  function focusables() {
    return Array.prototype.slice
      .call(
        dialog.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
      )
      .filter(function (el) {
        return el.offsetParent !== null;
      });
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var items = focusables();
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      close();
      return;
    }
    trapFocus(e);
  }

  function open() {
    if (isOpen) return;
    isOpen = true;

    lastFocused = document.activeElement;
    root.hidden = false;
    // Next frame, so the transition runs from the hidden state.
    requestAnimationFrame(function () {
      root.classList.add('is-open');
    });
    document.body.classList.add('cart-promo-popup-open');
    document.addEventListener('keydown', onKeydown, true);

    if (dialog) dialog.focus();
    if (config().oncePerSession !== false) sessionSet(SESSION_SHOWN_KEY, '1');
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;

    root.classList.remove('is-open');
    document.body.classList.remove('cart-promo-popup-open');
    document.removeEventListener('keydown', onKeydown, true);

    // Keep it out of the tab order once the fade-out finished.
    var hide = function () {
      if (!isOpen) root.hidden = true;
    };
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      hide();
    } else {
      setTimeout(hide, 250);
    }

    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  /* ===== Evaluate ===== */

  function evaluate() {
    var cfg = config();
    if (!cfg.productIds) return;

    var required = Number(cfg.requiredQty || 2);
    var oncePerSession = cfg.oncePerSession !== false;
    var alreadyShown = oncePerSession && sessionGet(SESSION_SHOWN_KEY) === '1';

    // Already shown and nothing on screen → nothing could change, skip the fetch.
    // Note we must NOT bail when it's open: an open popup still has to close when
    // the customer adds the third pack, or it stays there with the page scroll-locked.
    if (alreadyShown && !isOpen) return;

    Promise.all([getCart(), getIdSet()]).then(function (results) {
      var qty = eligibleQty(results[0], results[1]);
      var changed = qty !== lastQty;
      lastQty = qty;

      // Exactly at the trigger: below it the offer isn't in reach yet, above it
      // they already qualify and a popup would just be in the way.
      if (qty === required) {
        // Only on an actual change — the drawer's MutationObserver also fires for
        // DOM churn that left the cart alone, and re-opening a popup the customer
        // just closed is the fastest way to get it hated.
        if (changed && !alreadyShown) open();
      } else if (isOpen) {
        close();
      }
    });
  }

  function debouncedEvaluate() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(evaluate, 250);
  }

  /* ===== Wiring ===== */

  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-cpp-close]')) {
      e.preventDefault();
      close();
    }
  });

  // The CTA navigates away; don't let the popup flash again on the way out.
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-cpp-cta]')) sessionSet(SESSION_SHOWN_KEY, '1');
  });

  // Same trigger set the mini-cart uses: explicit events plus a DOM observer for
  // vendor add-to-cart paths that re-render the drawer without dispatching.
  document.addEventListener('cart:updated', debouncedEvaluate);
  document.addEventListener('cart:refresh', debouncedEvaluate);
  document.addEventListener('cart:promo-popup:changed', function () {
    idSet = null;
    topUpPromise = null;
    debouncedEvaluate();
  });

  var drawer =
    document.querySelector('.global-drawer__content') ||
    document.querySelector('#CartContainer') ||
    document.querySelector('.cart__items');

  if (drawer && 'MutationObserver' in window) {
    new MutationObserver(debouncedEvaluate).observe(drawer, { childList: true, subtree: true });
  }

  // No evaluation on load: the popup should react to the customer adding something,
  // not ambush them on arrival with a cart left over from last visit.
})();
